/** @license
 *
 * Copyright 2018 Esri
 *
 * All rights reserved under the copyright laws of the United States and applicable international laws, treaties,
 * and conventions.
 *
 * This material is licensed for use under the Esri Master License Agreement (MLA), and is bound by the terms of
 * that agreement. You may redistribute and use this code without modification, provided you adhere to the terms
 * of the MLA and include this copyright notice.
 *
 * See use restrictions at http://www.esri.com/legal/pdfs/mla_e204_e300/english
 *
 * For additional information, contact: Environmental Systems Research Institute, Inc. Attn: Contracts and Legal
 * Services Department 380 New York Street Redlands, California, USA 92373 USA
 *
 * email: contracts@esri.com
 */

/**
 * Auth manages an authenticated connection to ArcGIS.
 *
 * @module Auth
 */

//#region Interfaces
import * as common from "@esri/solution-common";
import IdentityManager from "@arcgis/core/identity/IdentityManager";
import OAuthInfo from "@arcgis/core/identity/OAuthInfo";
import Portal from "@arcgis/core/portal/Portal";
import { Logger, LogLevel } from "./Logger";

export interface IPortalSubset {
  name: string;
  id: string;
  restUrl: string;
  portalUrl: string;
  urlKey: string;
  hostName: string;
  orgUrl: string;
  locationTrackingEnabled: boolean;
  hubPremiumEnabled: boolean;
  workflowURL: string;
  isReadOnly: boolean;
}

export interface IPortalUserSubset {
  fullName: string;
  username: string;
  culture: string;
  thumbnailUrl: string;
  privileges: string[];
  orgId: string;
  role: string;
  roleId?: string;
}

interface IPortalUserWithPrivileges extends __esri.PortalUser {
  privileges: string[];
}

type ReplacerOption = ((key: string, value: any) => string) | HashMap<(value: any) => string>;

//#endregion
// ------------------------------------------------------------------------------------------------------------------ //

export class Auth {

  private _clientId: string;
  private _credential: __esri.Credential | null = null;
  private _locale: string = "en";
  private _portal: Portal;
  private _portalSubset: IPortalSubset | null = null;
  private _portalUrl: string;
  private _isEnterprise: boolean = false;
  private _portalUserSubset: IPortalUserSubset | null = null;
  private _logger: Logger;

  public constructor(
    clientId: string,
    portalURL: string,
    locale: string,
    logger: Logger
  ) {
    this._clientId = clientId;
    this._locale = locale;
    this._portalUrl = portalURL;
    this._logger = logger;

    // Use origin for the portal Url if it's on arcgis.com domain
    // Else use the portalUrl passed in as the default. Default is https://www.arcgis.com
    this._portalUrl = location.origin.endsWith(".arcgis.com") ? window.location.origin : portalURL;

    const info = new OAuthInfo({
      appId: clientId,
      locale: this._locale,
      popup: false, // inline redirects don't require any additional app configuration
      portalUrl: this._portalUrl,
      preserveUrlHash: true
    });

    IdentityManager.registerOAuthInfos([info]);

    this._portal = new Portal({ url: this._portalUrl });
  }

  /**
   * Initializes the Auth object. This method must be called before any other Auth methods.
   * If the user is not authorized to use the app, the page will be redirected to an error page.
   *
   * @returns Promise that resolves with the initialized Auth object
   */
  public async init(
  ): Promise<Auth> {
    try {
      await this._init();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      // User is not signed in or not authorized to use the app
      this.destroy();
    }
    return this;
  }

  /**
   * Initializes the Auth object. This method must be called before any other Auth methods.
   * If the user is not authorized to use the app, the page will be redirected to an error page.
   *
   * @returns Promise that resolves with the initialized Auth object
   */
  private async _init(
  ): Promise<Auth> {
    // Persist logins when the page is refreshed
    this._credential = await IdentityManager.checkSignInStatus(this._portalUrl + "/sharing");

    // Check that this user is authorized to use the app
    try {
      const response = await IdentityManager.checkAppAccess(this._portalUrl, this._clientId);
      if ((typeof response.valid !== "undefined" && !response.valid) || response.viewOnlyUserTypeApp) {
        // User is not authorized to use the app
        this._logger.log(LogLevel.Info, "User is not authorized to use the app");
        window.location.replace(`error.html?error=blocked&locale=${this._locale}`);
      }
      this._logger.log(LogLevel.Info, "User is authorized to use the app");
    } catch (err: any) {
      // User is not authorized to use the app
      this._logger.log(LogLevel.Errors, err.originalMessage || err);
      window.location.replace(`error.html?error=blocked&locale=${this._locale}`);
      }

    this._portal.authMode = "immediate";

    // Once portal is loaded, user signed in
    await this._portal.load()
    this._isEnterprise = this._portal.isPortal;

    this._portalSubset = {
      name: this._portal.name,
      id: this._portal.id,
      restUrl: this._portal.restUrl,
      portalUrl: this._portal.url,
      urlKey: this._portal.urlKey,
      hostName: this._portal.portalHostname,
      orgUrl: this._getOrgUrl(this._portal.url, this._portal.urlKey, this._portal.customBaseUrl),
      locationTrackingEnabled: this._portal.helperServices.hasOwnProperty("locationTracking"),
      hubPremiumEnabled: this._portal.portalProperties?.hub?.enabled || false,
      workflowURL: this._portal.helperServices?.workflowManager?.url ?? this._portal.url,
      isReadOnly: this._portal.isReadOnly
    };

    this._portalUserSubset = {
      fullName: this._portal.user.fullName,
      username: this._portal.user.username,
      culture: this._portal.user.culture?.toLowerCase() || "en",
      thumbnailUrl: this._portal.user.thumbnailUrl ||
        this._portal.url + "/home/js/arcgisonline/css/images/no-user-thumb.jpg",
      orgId: this._portal.user.orgId,
      privileges: (this._portal.user as IPortalUserWithPrivileges).privileges,
      role: this._portal.user.role
    };

    // If running on Enterprise, update the workflow URL
    if (this._isEnterprise) {
      this._portalSubset.workflowURL =
        await common.getWorkflowEnterpriseServerRootURL(
          this._portal.restUrl, this.RestUserSession || new common.UserSession({}));
    }

    if (this._portal.user.roleId) {
      this._portalUserSubset.roleId = this._portal.user.roleId
    }

    return this;
  }

  public get SignedIn(
  ): boolean {
    return this._credential !== null;
  }

  public get Credential(
  ): __esri.Credential | null {
    return this._credential;
  }

  public get Portal(
  ): Portal {
    return this._portal;
  }

  public get PortalUrl(
  ): string {
    return this._portalUrl;
  }

  public get PortalSubset(
  ): IPortalSubset | null {
    return this._portalSubset;
  }

  public get PortalUserSubset(
  ): IPortalUserSubset | null {
    return this._portalUserSubset;
  }

  public get PortalUserOrg(
  ): string | null {
    return this._portalSubset && this._portalSubset.id;
  }

  public get PortalUserName(
  ): string {
    return (this._portalUserSubset && this._portalUserSubset.username) || "";
  }

  public get PortalUserCulture(
  ): string {
    return (this._portalUserSubset && this._portalUserSubset.culture) || "";
  }

  public get PortalUserPrivileges(
  ): string[] {
    let privileges = [] as string[];
    if (this._portalUserSubset?.privileges) {
      privileges = this._portalUserSubset.privileges;
    }
    return privileges;
  }

  public get IsEnterprisePortal(
  ): boolean {
    return this._isEnterprise;
  }

  /**
   * Translates authentication from the format used in the [ArcGIS API for JavaScript](https://developers.arcgis.com/javascript/).
   *
   * @returns UserSession
   */
  public get RestUserSession(
  ): common.UserSession | null {
    const credential = this.Credential;
    if (!credential) {
      return null;
    }
    const userSession = common.UserSession.fromCredential(credential);

    // Fill in missing fields
    (userSession as any).clientId = (this._credential as any)._oAuthCred?.appId;
    (userSession as any)._refreshToken = (this._credential as any)._oAuthCred?.refreshToken;

    // Patch getDomainCredentials to handle URLs with mismatching cases
    userSession.getDomainCredentials = function (url) {
      const trustedDomains: string[] = (userSession as any)["trustedDomains"] || [];
      if (!trustedDomains || !trustedDomains.length) {
        return "same-origin";
      }

      url = url.toLocaleLowerCase();
      return trustedDomains.some(function (domainWithProtocol: string) {
        return url.startsWith(domainWithProtocol.toLocaleLowerCase());
      })
        ? "include"
        : "same-origin";
    };

    return userSession;
  }

  public refreshSession(
  ): Promise<common.UserSession> | null {
    const userSession = this.RestUserSession;
    if (!userSession) {
      return null;
    }

    return userSession.refreshSession();
  }

  /**
   * Signs into app and refreshes the window.location or switches to the supplied URL.
   *
   * @param signInParamValue If supplied, the parameter `signInParam` with the supplied value
   * will be added to the redirect uri's search params
   */
  public signIn(
    signInParamValue = ""
  ) {
    // If a sign-in parameter value is supplied, add it to the redirect uri before
    // the authentication display appears
    IdentityManager.setOAuthRedirectionHandler(info => {
      const redirectUri = info.authorizeParams.redirect_uri as string;
      if (signInParamValue.length > 0) {
        // Add the sign-in parameter to the redirect uri
        const redirectUriParts = redirectUri.split("?");
        const redirectOriginPath = redirectUriParts[0];
        let redirectSearch = "";
        if (redirectUriParts[1]) {
          const redirectSearchParams = new URLSearchParams(redirectUriParts[1]);
          redirectSearchParams.append("signInParam", signInParamValue);
          redirectSearch = "?" + redirectSearchParams.toString();
        }
        info.authorizeParams.redirect_uri = redirectOriginPath + redirectSearch;
      }
      window.location.href = info.authorizeUrl + "?" + this._objectToQuery(info.authorizeParams as HashMap<any>);
      return;
    });

    // getCredential usually triggers a page refresh. If it doesn't refresh but returns a credential, we'll do the
    // refresh; if it doesn't refresh but doesn't return a credential, we'll leave the page alone

    void IdentityManager.getCredential((this._portalSubset?.portalUrl ?? this._portalUrl) + "/sharing")
    .then(
      () => {
        // Got a credential without a redirect, so we'll do refresh
        window.location.reload();
      }
    );
  }

  /**
   * Signs out of app and changes page to the portal host.
   */
  public signOut(
  ): void {
    let redirectUrl = "https://" + this._portalSubset?.hostName;
    if (window.location.hostname === this._portalSubset?.hostName) {
      // User is logged into App at www.arcgis.com/apps/solutions/ path
      // redirect to portal index url
      redirectUrl += "/index.html";
    }
    else if (this._portalSubset?.urlKey && window.location.hostname.indexOf(this._portalSubset?.urlKey) > -1) {
      // User is logged into App at orgurlkey.maps.arcgis.com/apps/solutions/
      // Redirect to portal home url
      redirectUrl = window.location.origin + "/home/index.html";
    }
    else {
      redirectUrl = this._portalUrl + "/home";
    }

    // N.B.: This closes the app and redirects to a new page
    this.signOutAndRedirect(redirectUrl);
  }

  /**
   * Signs out of app and changes page to the supplied URL.
   *
   * @param redirectUrl Page to replace the current page
   */
  public signOutAndRedirect(
    redirectUrl: string
  ): void {
    // N.B.: This closes the app and redirects to specified page
    const restUrl: string = this._portalSubset?.restUrl ?? this._portalUrl + "/sharing/rest";
    const signOutUrl =
      `${restUrl}/oauth2/signout?client_id=${this._clientId}&redirect_uri=${encodeURIComponent(redirectUrl)}`;
    this.destroy();
    window.location.replace(signOutUrl);
  }

  public destroy(
  ): void {
    this._credential = null;
    this._portalSubset = null;
    this._portalUserSubset = null;
    IdentityManager.destroyCredentials();
  }

  private _getOrgUrl(
    portalUrl: string,
    portalUrlKey: string,
    portalCustomBaseUrl: string,
  ): string {
    if (portalUrlKey && portalCustomBaseUrl) {
      return window.location.protocol + "//" + portalUrlKey.toLowerCase() + "." + portalCustomBaseUrl;
    }
    else {
      return portalUrl;
    }
  }

  // Copied from function exported in arcgis-js-api but not added to JSAPI.
  private _isSerializable(obj: any): obj is { toJSON(): any } {
    return obj && typeof obj === "object" && "toJSON" in obj && typeof obj.toJSON === "function";
  }

  /**
   * Converts a hashmap of query parameters to a query string. Following formatting rules applies:
   *   - object values that implements `toJSON()` are stringified
   *   - array value will map to multiple key value pairs. ie `{ key: ["v1", "v2"] }` returns `key=v1&key=v2
   *
   * @param queryParameters the query parameters to convert to a string
   * @param replacers optional replacer functions defined per key
   *
   * @example
   * const queryParameters = {
   *   objectIds: [1, 2, 3, 4]
   * };
   *
   * // returns: "objectIds=1&objectIds=2&objectIds=3&objectIds=4"
   * objectToQuery(queryParameters);
   *
   * // returns: "objectIds=1,2,3,4"
   * objectToQuery(queryParameters, {
   *   objectIds: (v) => v.join(",")
   * });
   *
   * // returns: "objectIds=1,2,3,4"
   * objectToQuery(queryParameters, (k, v) => {
   *   switch (k) {
   *     case "objectIds":
   *       return v.join(","));
   *     default:
   *       return "" + v;
   *   }
   * });
   *
   * Copied from function exported in arcgis-js-api but not added to JSAPI.
   */
  private _objectToQuery(queryParameters: HashMap<any>, replacers?: ReplacerOption): string {
    if (!queryParameters) {
      return "";
    }

    if (replacers && typeof replacers === "function") {
      return Object.keys(queryParameters)
        .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(replacers(key, queryParameters[key])))
        .join("&");
    }

    return Object.keys(queryParameters)
      .map((key) => {
        const value = queryParameters[key];

        if (value == null) {
          return "";
        }

        const assign = encodeURIComponent(key) + "=";
        const replacer = replacers && replacers[key];

        if (replacer) {
          return assign + encodeURIComponent(replacer(value));
        }

        if (Array.isArray(value)) {
          return value
            .map((child) => {
              if (this._isSerializable(child)) {
                return assign + encodeURIComponent(JSON.stringify(child));
              }

              return assign + encodeURIComponent(child as string);
            })
            .join("&");
        }

        if (this._isSerializable(value)) {
          return assign + encodeURIComponent(JSON.stringify(value));
        }

        return assign + encodeURIComponent(value as string);
      })
      .filter((value) => value)
      .join("&");
  }

}
